// Enthalpy.js — Mass-specific enthalpies using IG Cp + PRSV departure.
// Exports:
//   - hVap(y, T, comps, P=101325)  // kJ/kg
//   - hLiq(x, T, comps, P=101325)  // kJ/kg
//
// Uses solvePRSV_mixture() from EOSK.js to get mixture departure enthalpy (J/mol).

import { solvePRSV_mixture } from "./EOSK";

// Rough ideal-gas Cp per component (J/mol/K). Replace with better data when available.
function CpIG(comp, T) {
  const MW = comp.MW ?? 200; // g/mol
  // Mild dependence on size and temperature; keeps numbers sane across heavy ends.
  const base = 20 + 0.05 * Math.max(0, Math.min(400, T - 300) / 100); // ~20–22 J/mol/K
  const size = Math.min(60, 0.06 * Math.max(0, MW - 20)); // up to +60
  return base + size; // J/mol/K
}

// Average molar mass (kg/mol) of a mixture vector
function avgMW(x, comps) {
  let M = 0;
  for (let i = 0; i < x.length; i++) M += x[i] * (comps[i].MW ?? 200);
  return Math.max(1e-12, M) / 1000; // kg/mol
}

// Ideal-gas sensible enthalpy (J/mol) with Tref≈0 for simplicity
function hIG_molar(x, T, comps) {
  let H = 0;
  for (let i = 0; i < x.length; i++) {
    H += x[i] * CpIG(comps[i], T) * T;
  }
  return H; // J/mol
}

function to_kJ_per_kg(H_J_per_mol, MWbar_kg_per_mol) {
  return H_J_per_mol / MWbar_kg_per_mol / 1000; // kJ/kg
}

// Phase enthalpy (kJ/kg): IG sensible + EOS departure
function hPhase(x, T, P, comps, phase) {
  const { hdepL, hdepV } = solvePRSV_mixture({ P, T, x: x.slice(), comps });
  const Hdep = phase === "V" ? hdepV : hdepL; // J/mol
  const Hig = hIG_molar(x, T, comps); // J/mol
  const Hm = Hig + Hdep; // J/mol
  const MWb = avgMW(x, comps); // kg/mol
  return to_kJ_per_kg(Hm, MWb);
}

export function hVap(y, T, comps, P = 101325) {
  return hPhase(y, T, P, comps, "V");
}

export function hLiq(x, T, comps, P = 101325) {
  return hPhase(x, T, P, comps, "L");
}
